import { Alert, MultiSelect } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchCartographyTree from 'src/Hooks/Queries/useFetchCartographyTree';
import { trackStayRoomConflictShown } from 'src/Services/Mixpanel/events';
import { isSleepingRoomType } from 'src/types/cartography.types';

import styles from './RoomSelector.module.scss';
import { Props } from './RoomSelector.types';

/**
 * Chambres attribuées à un séjour — « quelle chambre pour qui ».
 *
 * Ne propose que les pièces qui portent un couchage : le back refuse les autres en 400, et
 * offrir la buanderie dans la liste serait promettre ce qu'il rejette. Les chambres sont
 * groupées par bâtiment, seul repère utile quand deux d'entre elles portent le même nom.
 *
 * Lorsqu'un 409 EVENT_ROOM_CONFLICT remonte, les conflits sont affichés nominativement au-dessus
 * du sélecteur ; c'est la façon la plus directe de dire « non, pas ces nuits-là, pas cette
 * chambre, pas face à cette personne ».
 */
const RoomSelector = ({ value, onChange, label, placeholder, conflicts }: Props) => {
  const { t, i18n } = useTranslation();
  const { data: tree, isLoading } = useFetchCartographyTree();
  const lastTrackedSignature = useRef<string>('');

  const groups = useMemo(() => {
    return (tree?.buildings ?? [])
      .map(building => ({
        group: building.name,
        // Toutes les pièces d'un bâtiment vivent sur lui, l'étage n'étant qu'une référence
        // portée par la pièce (`floorId`).
        items: building.rooms
          .filter(room => isSleepingRoomType(room.type) && !room.isOutOfService)
          .map(room => ({ value: room.id, label: room.name })),
      }))
      .filter(group => group.items.length > 0);
  }, [tree]);

  const hasRooms = groups.length > 0;

  const conflictLines = useMemo(() => {
    if (!conflicts || conflicts.length === 0) return [];
    const locale = i18n.language;
    return conflicts.map(c => {
      const start = dayjs(c.startTime).locale(locale).format('D MMM YYYY');
      const end = dayjs(c.endTime).locale(locale).format('D MMM YYYY');
      return c.createdByName
        ? t('createEventForm.rooms.conflictLineWithOwner', {
            defaultValue: '{{room}} déjà prise par {{owner}} du {{start}} au {{end}}',
            room: c.roomName,
            owner: c.createdByName,
            start,
            end,
          })
        : t('createEventForm.rooms.conflictLine', {
            defaultValue: '{{room}} déjà prise du {{start}} au {{end}}',
            room: c.roomName,
            start,
            end,
          });
    });
  }, [conflicts, i18n.language, t]);

  useEffect(() => {
    if (!conflicts || conflicts.length === 0) {
      lastTrackedSignature.current = '';
      return;
    }
    const signature = conflicts.map(c => `${c.roomId}:${c.eventName}`).join('|');
    if (signature === lastTrackedSignature.current) return;
    lastTrackedSignature.current = signature;
    const uniqueRooms = new Set(conflicts.map(c => c.roomId)).size;
    const uniqueEvents = new Set(conflicts.map(c => c.eventName)).size;
    trackStayRoomConflictShown({
      room_count: uniqueRooms,
      conflicting_event_count: uniqueEvents,
    });
  }, [conflicts]);

  return (
    <div className={styles.container}>
      {conflictLines.length > 0 && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          className={styles.conflictAlert}
          title={t(
            'createEventForm.rooms.conflictTitle',
            'Cette chambre est déjà prise sur cette période'
          )}
        >
          <ul className={styles.conflictList}>
            {conflictLines.map((line, i) => (
              <li key={i} className={styles.conflictItem}>
                {line}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      <MultiSelect
        label={label}
        placeholder={
          hasRooms
            ? placeholder
            : t('createEventForm.rooms.none', 'Aucune chambre enregistrée dans la cartographie')
        }
        data={groups}
        value={value}
        onChange={onChange}
        disabled={isLoading || !hasRooms}
        searchable
        clearable
      />
    </div>
  );
};

export default RoomSelector;
